"use client"

import type React from "react"
import { useState, useEffect } from "react"
import { Link } from "react-router-dom"
import Navbar from "../components/Navbar"
import "./Home.css"
import axios from "axios"
import Footer from "../components/Footer"


interface Product {
  id: number
  name: string
  price: number
  description: string
  images: { image_url: string }[]
  category?: string
  pickup_location?: string
  created_at?: string
  seller_username?: string
}

const Home: React.FC = () => {
  const [products, setProducts] = useState<Product[]>([])
  const [filteredProducts, setFilteredProducts] = useState<Product[]>([])
  const [searchTerm, setSearchTerm] = useState("")
  const [priceRange, setPriceRange] = useState({ min: "", max: "" })
  const [selectedCategory, setSelectedCategory] = useState("")
  const [selectedLocation, setSelectedLocation] = useState("")
  const [selectedDatePosted, setSelectedDatePosted] = useState("")

  useEffect(() => {
    const fetchProducts = async () => {
      try {
        const response = await axios.get("https://new-thriftshop.onrender.com/api/products/", {
          headers: {
            "Content-Type": "application/json",
          },
          withCredentials: false,
        })

        setProducts(response.data.products)
      } catch (error) {
        console.error("Failed to fetch products:", error)
      }
    }

    fetchProducts()
  }, [])

  useEffect(() => {
    let result = [...products]

    // Search filter
    if (searchTerm) {
      result = result.filter(
        (product) =>
          product.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
          product.description?.toLowerCase().includes(searchTerm.toLowerCase())
      )
    }

    // Price filter
    if (priceRange.min) {
      result = result.filter((product) => product.price >= Number(priceRange.min))
    }
    if (priceRange.max) {
      result = result.filter((product) => product.price <= Number(priceRange.max))
    }

    // Category filter (case-insensitive)
    if (selectedCategory) {
      result = result.filter(
        (product) => product.category?.toLowerCase() === selectedCategory.toLowerCase()
      )
    }

    // Date Posted filter
    if (selectedDatePosted) {
      const now = new Date()
      result = result.filter((product) => {
        const createdAt = new Date(product.created_at ?? "")
        const timeDiff = now.getTime() - createdAt.getTime()
        const oneDay = 24 * 60 * 60 * 1000
        const oneWeek = 7 * oneDay
        const oneMonth = 30 * oneDay

        switch (selectedDatePosted) {
          case "24-hours":
            return timeDiff <= oneDay
          case "this-week":
            return timeDiff <= oneWeek
          case "last-month":
            return timeDiff <= oneMonth
          default:
            return true
        }
      })
    }

    setFilteredProducts(result)
  }, [searchTerm, priceRange, selectedCategory, selectedLocation, selectedDatePosted, products])

  const handleSearch = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(e.target.value)
  }

  const handlePriceChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target
    setPriceRange((prev) => ({ ...prev, [name]: value }))
  }

  return (
    <>
      <Navbar />
      <div className="home-container">
        <div className="container">
          <div className="home-content">
          <div className="sidebar">
              <h3>Filters</h3>

              {/* Price Filter */}
              <div className="filter-section">
                <h4>Price Range, ₱</h4>
                <div className="price-inputs">
                  <div className="price-input-wrapper">
                    <label htmlFor="price-min">Minimum Price</label>
                    <input
                      id="price-min"
                      type="number"
                      name="min"
                      placeholder="0"
                      value={priceRange.min}
                      onChange={handlePriceChange}
                      className="price-input"
                      min="0"
                      step="0.01"
                    />
                  </div>
                  <div className="price-input-wrapper">
                    <label htmlFor="price-max">Maximum Price</label>
                    <input
                      id="price-max"
                      type="number"
                      name="max"
                      placeholder="999"
                      value={priceRange.max}
                      onChange={handlePriceChange}
                      className="price-input"
                      min="0"
                      step="0.01"
                    />
                  </div>
                </div>
              </div>

              {/* Category Filter */}
              <div className="filter-section">
                <h4>Category</h4>
                <div className="filter-options">
                  {["Top", "Bottom", "Accessories", "Shoes", "Vest", "Dress"].map((cat) => (
                    <label className="filter-option" key={cat}>
                      <input
                        type="radio"
                        name="category"
                        value={cat}
                        checked={selectedCategory === cat}
                        onChange={() => setSelectedCategory(cat)}
                      />
                      {cat.charAt(0).toUpperCase() + cat.slice(1)}
                    </label>
                  ))}
                  <label className="filter-option">
                    <input
                      type="radio"
                      name="category"
                      value=""
                      checked={selectedCategory === ""}
                      onChange={() => setSelectedCategory("")}
                    />
                    All Categories
                  </label>
                </div>
              </div>

              {/* Date Filter */}
              <div className="filter-section">
                <h4>Date Posted</h4>
                <div className="filter-options">
                  {[
                    { label: "24 hours", value: "24-hours" },
                    { label: "This week", value: "this-week" },
                    { label: "Last month", value: "last-month" },
                  ].map(({ label, value }) => (
                    <label className="filter-option" key={value}>
                      <input
                        type="radio"
                        name="datePosted"
                        value={value}
                        checked={selectedDatePosted === value}
                        onChange={() => setSelectedDatePosted(value)}
                      />
                      {label}
                    </label>
                  ))}
                  <label className="filter-option">
                    <input
                      type="radio"
                      name="datePosted"
                      value=""
                      checked={selectedDatePosted === ""}
                      onChange={() => setSelectedDatePosted("")}
                    />
                    All Time
                  </label>
                </div>
              </div>
            </div>

            <div className="main-content">
              <div className="search-bar">
                <input
                  type="text"
                  placeholder="Search for a product"
                  value={searchTerm}
                  onChange={handleSearch}
                  className="search-input"
                />
              </div>

              <div className="products-grid">
      {filteredProducts.length > 0 ? (
        filteredProducts.map((product) => (
          <div key={product.id} className="product-card" style={{ backgroundColor: "rgba(163, 25, 36, 0.3)" , boxShadow: "0 0 20px rgba(163, 25, 36, 0.3)" }}
>
            <div className="product-image-container">
              <img
                src={product.images.length > 0 ? product.images[0].image_url : "/placeholder.png"}
                alt={product.name}
                className="product-image"
                loading="lazy"
              />
              <div className="product-overlay">
                
              </div>
            </div>
            <div className="product-info-home">
              <div className="product-header">
                <h3 className="product-name-home" title={product.name} style={{ color: "var(--gothic-text)" }}>
                  {product.name}
                </h3>
                <div className="product-price-container-home">
                  <span className="product-price-home">₱{product.price.toFixed(2)}</span>
                </div>
              </div>
              <p className="product-description-home" title={product.description} style={{ color: "var(--gothic-text)" }}>
                {product.description.length > 120 ? `${product.description.substring(0, 120)}...` : product.description}
              </p>
              <div className="product-actions">               
              <Link to={`/product/${product.id}`} className="view-details-btn">                  <span>View Details</span>
                  <svg className="btn-arrow" width="16" height="16" viewBox="0 0 16 16" fill="none">
                    <path
                      d="M6 12L10 8L6 4"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                </Link>
              </div>
            </div>
          </div>
        ))
      ) : (
        <div className="no-products">
          <div className="no-products-icon">
            <svg width="64" height="64" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
              <circle cx="11" cy="11" r="8" />
              <path d="M21 21l-4.35-4.35" />
            </svg>
          </div>
          <h3>No products found</h3>
          <p>Try adjusting your search criteria or browse our categories.</p>
        </div>
      )}
    </div>
            </div>
          </div>
        </div>
      </div>
      <Footer/>
    </>
  )
}

export default Home
